import { Button, Input } from '@/components/ui'
import { AlertCircle, ArrowRight, Building2, CheckCircle2, Loader2, LockKeyhole, Mail, XCircle } from 'lucide-react'

import type { AxiosError } from 'axios'
import { checkSlug, signup } from '@/api/services/auth.service'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/useAuthStore'
import { queryClient } from '@/main'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@/lib/cn'
import { isTenantSubdomainsEnabled, tenantBaseDomain, tenantUrl } from '@/lib/tenantHost'
import { PASSWORD_HINT, newPasswordSchema } from '@/lib/passwordPolicy'
import type { SignupPreview } from '@/features/auth/types/signupPreview'

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

const signupSchema = z
  .object({
    organizationName: z
      .string()
      .min(2, 'Organization name must be at least 2 characters')
      .max(100, 'Organization name is too long'),
    organizationSlug: z
      .string()
      .min(2, 'Slug must be at least 2 characters')
      .max(50, 'Slug is too long')
      .regex(
        SLUG_PATTERN,
        'Lowercase letters, numbers and hyphens only'
      ),
    firstName: z.string().min(1, 'First name is required').max(50),
    lastName: z.string().min(1, 'Last name is required').max(50),
    email: z.string().min(1, 'Email is required').email('Invalid email address'),
    password: newPasswordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type SignupFormValues = z.infer<typeof signupSchema>

const STRENGTH_LABELS = ['Too short', 'Weak', 'Fair', 'Good', 'Strong'] as const
const STRENGTH_COLORS = ['bg-danger', 'bg-danger', 'bg-warning', 'bg-primary-light', 'bg-success'] as const

/** 0–4: length gates the score, variety raises it. */
function passwordStrength(password: string): number {
  if (password.length < 8) return 0
  let score = 1
  if (password.length >= 12) score++
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++
  if (/\d/.test(password) && /[^A-Za-z0-9]/.test(password)) score++
  return score
}

interface SignupFormProps {
  /** Called as the visitor types, so the page can mirror it in a preview. */
  onPreviewChange?: (preview: SignupPreview) => void
}

/**
 * Organization signup form. Provisions a new organization with the
 * submitter as its owner admin, then lands them in the dashboard.
 */
export function SignupForm({ onPreviewChange }: SignupFormProps = {}): React.ReactElement {
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)
  const [apiError, setApiError] = useState<string | null>(null)
  const [slugTouched, setSlugTouched] = useState(false)
  const [debouncedSlug, setDebouncedSlug] = useState('')

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      organizationName: '',
      organizationSlug: '',
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  })

  const organizationName = watch('organizationName')
  const organizationSlug = watch('organizationSlug')
  const firstName = watch('firstName')
  const lastName = watch('lastName')
  const email = watch('email')
  const password = watch('password')

  useEffect(() => {
    onPreviewChange?.({ organizationName, organizationSlug, firstName, lastName, email })
  }, [onPreviewChange, organizationName, organizationSlug, firstName, lastName, email])

  // Keep the slug in sync with the name until the user edits it manually.
  useEffect(() => {
    if (!slugTouched) {
      setValue('organizationSlug', toSlug(organizationName))
    }
  }, [organizationName, slugTouched, setValue])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSlug(organizationSlug), 400)
    return () => clearTimeout(timer)
  }, [organizationSlug])

  const slugIsCheckable =
    debouncedSlug.length >= 2 && SLUG_PATTERN.test(debouncedSlug)
  const slugQuery = useQuery({
    queryKey: ['check-slug', debouncedSlug],
    queryFn: () => checkSlug(debouncedSlug),
    enabled: slugIsCheckable,
    staleTime: 30_000,
    retry: false,
  })
  const slugStatus =
    !slugIsCheckable || debouncedSlug !== organizationSlug
      ? null
      : slugQuery.data

  const mutation = useMutation({
    mutationFn: signup,
    onSuccess: (data) => {
      toast.success(`Welcome to ${data.organization.name}!`)
      queryClient.clear()
      setAuth(data.user)
      // The new organization lives on its own subdomain from the start.
      if (isTenantSubdomainsEnabled()) {
        window.location.replace(tenantUrl(data.organization.slug, '/dashboard'))
        return
      }
      navigate('/dashboard', { replace: true })
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message =
        error.response?.data?.message ?? 'Signup failed. Please try again.'
      setApiError(message)
    },
  })

  const onSubmit = (values: SignupFormValues): void => {
    setApiError(null)
    mutation.mutate({
      organizationName: values.organizationName,
      organizationSlug: values.organizationSlug,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      password: values.password,
    })
  }

  const inputClassName =
    'h-12 text-base caret-primary md:text-sm dark:border-gray-300 dark:bg-white dark:text-gray-900 dark:placeholder:text-gray-500'
  const baseDomain = tenantBaseDomain()
  const strength = passwordStrength(password)
  const slugChecking = slugIsCheckable && slugQuery.isFetching
  const slugField = register('organizationSlug', {
    onChange: () => setSlugTouched(true),
  })

  return (
    <form
      method="post"
      action="."
      onSubmit={(e) => {
        e.preventDefault()
        void handleSubmit(onSubmit)(e)
      }}
      className="space-y-8"
    >
      {apiError && (
        <div
          role="alert"
          aria-live="polite"
          className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-800"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span className="leading-5">{apiError}</span>
        </div>
      )}

      <fieldset className="space-y-5">
        <legend className="sr-only">Your organization</legend>
        <p className="text-sm font-semibold text-[var(--public-ink)]" aria-hidden>
          Your organization
        </p>
        <Input
          label="Organization name"
          type="text"
          autoComplete="organization"
          placeholder="e.g. Acme Inc"
          leadingIcon={<Building2 className="h-4 w-4" />}
          error={errors.organizationName?.message}
          className={inputClassName}
          {...register('organizationName')}
        />
        <div>
          <label
            htmlFor="organizationSlug"
            className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500"
          >
            Workspace address
          </label>
          <div
            className={cn(
              'flex h-12 w-full items-center overflow-hidden rounded-xl border bg-white shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-150 focus-within:ring-4',
              errors.organizationSlug || slugStatus?.available === false
                ? 'border-danger focus-within:ring-danger/10'
                : 'border-gray-200 focus-within:border-primary/40 focus-within:ring-primary/10'
            )}
          >
            <input
              id="organizationSlug"
              type="text"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="acme-inc"
              aria-invalid={!!errors.organizationSlug || slugStatus?.available === false}
              aria-describedby="organizationSlug-status"
              className="h-full min-w-0 flex-1 bg-transparent px-3.5 text-base text-gray-900 caret-primary placeholder:text-gray-400 focus:outline-none md:text-sm"
              {...slugField}
            />
            {baseDomain && (
              <span className="flex h-full shrink-0 items-center border-l border-gray-200 bg-[var(--public-bg)] px-3.5 text-sm text-[var(--public-muted)]">
                .{baseDomain}
              </span>
            )}
          </div>
          <p
            id="organizationSlug-status"
            className={cn(
              'mt-1.5 flex items-center gap-1.5 text-xs',
              errors.organizationSlug
                ? 'text-danger'
                : slugStatus?.available
                  ? 'text-success'
                  : slugStatus
                    ? 'text-danger'
                    : 'text-[var(--public-muted)]'
            )}
            aria-live="polite"
          >
            {errors.organizationSlug ? (
              errors.organizationSlug.message
            ) : slugChecking ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                Checking availability…
              </>
            ) : slugStatus?.available ? (
              <>
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                &ldquo;{slugStatus.slug}&rdquo; is available
              </>
            ) : slugStatus ? (
              <>
                <XCircle className="h-3.5 w-3.5" aria-hidden />
                &ldquo;{slugStatus.slug}&rdquo; is taken. Try another.
              </>
            ) : (
              'Your team uses this address to sign in.'
            )}
          </p>
        </div>
      </fieldset>

      <fieldset className="space-y-5 border-t border-[var(--public-border)] pt-8">
        <legend className="sr-only">Your admin account</legend>
        <p className="text-sm font-semibold text-[var(--public-ink)]" aria-hidden>
          Your admin account
        </p>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Input
            label="First name"
            type="text"
            autoComplete="given-name"
            error={errors.firstName?.message}
            className={inputClassName}
            {...register('firstName')}
          />
          <Input
            label="Last name"
            type="text"
            autoComplete="family-name"
            error={errors.lastName?.message}
            className={inputClassName}
            {...register('lastName')}
          />
        </div>
        <Input
          label="Work email"
          type="email"
          autoComplete="email"
          placeholder="you@company.com"
          leadingIcon={<Mail className="h-4 w-4" />}
          error={errors.email?.message}
          className={inputClassName}
          {...register('email')}
        />
        <div>
          <Input
            label="Password"
            type="password"
            autoComplete="new-password"
            placeholder="At least 8 characters"
            showPasswordToggle
            hint={PASSWORD_HINT}
            leadingIcon={<LockKeyhole className="h-4 w-4" />}
            error={errors.password?.message}
            className={inputClassName}
            {...register('password')}
          />
          {password && !errors.password && (
            <div className="mt-2 flex items-center gap-3" aria-live="polite">
              <div className="grid flex-1 grid-cols-4 gap-1" aria-hidden>
                {[1, 2, 3, 4].map((step) => (
                  <span
                    key={step}
                    className={cn(
                      'h-1 rounded-full transition-colors duration-200',
                      strength >= step ? STRENGTH_COLORS[strength] : 'bg-gray-200'
                    )}
                  />
                ))}
              </div>
              <span className="w-16 text-right text-xs font-medium text-[var(--public-muted)]">
                {STRENGTH_LABELS[strength]}
              </span>
            </div>
          )}
        </div>
        <Input
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          showPasswordToggle
          leadingIcon={<LockKeyhole className="h-4 w-4" />}
          error={errors.confirmPassword?.message}
          className={inputClassName}
          {...register('confirmPassword')}
        />
      </fieldset>

      <Button
        type="submit"
        size="lg"
        className="group h-12 w-full touch-manipulation rounded-xl text-[0.9375rem] font-semibold"
        disabled={slugStatus?.available === false}
        loading={mutation.isPending}
        loadingLabel="Creating organization"
      >
        {mutation.isPending ? (
          'Creating organization…'
        ) : (
          <>
            Create organization
            <ArrowRight
              className="ml-0.5 h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5"
              aria-hidden
            />
          </>
        )}
      </Button>
    </form>
  )
}
